// =====================================================================
// 🏠 SERVICIO A DOMICILIO
// ---------------------------------------------------------------------
// Se activa por empresa desde SuperAdmin (enabledFeatures.domicilio) y
// después la barbería lo prende en Servicios → Servicio a domicilio, donde
// elige la categoría de servicios a domicilio (ej: "A domicilio", con
// sus servicios y precios normales), hasta qué hora atiende y qué
// ciudades cubre.
// La configuración vive en shopSettings.domicilio (por local) y la
// ubicación del cliente se guarda en la reserva (campo "domicilio" del
// documento principal). No usa ninguna API paga de Google: la ubicación
// sale del GPS del celular o de un link de ubicación que pega el cliente,
// y el "Cómo llegar" es un link común a Maps.
// =====================================================================

export const CIUDADES_GRAN_ASUNCION = [
  'Asunción',
  'Lambaré',
  'Fernando de la Mora',
  'San Lorenzo',
  'Luque',
  'Mariano Roque Alonso',
  'Limpio',
  'Capiatá',
  'Ñemby',
  'Villa Elisa',
  'San Antonio',
];

export const DOMICILIO_DEFAULT = {
  activo: false,
  categoria: '',
  horaFin: '22:00',
  ciudades: CIUDADES_GRAN_ASUNCION,
  // Personas adicionales en la misma visita: [{ id, nombre, precio, duracion }]
  // ej: "1 servicio" Gs 150.000 / 60 min, "2 servicios" Gs 200.000 / 120 min
  adicionales: [],
  maxPersonas: 5, // contando a la primera persona (la de la visita base)
};

export const getDomicilioConfig = (shopSettings) => ({
  ...DOMICILIO_DEFAULT,
  ...(shopSettings?.domicilio || {}),
});

// ¿La barbería ofrece domicilio ahora mismo? (habilitado por Joel + prendido por el local)
export const domicilioDisponible = (enabledFeatures, shopSettings) =>
  !!enabledFeatures?.domicilio && !!shopSettings?.domicilio?.activo;

// 👥 Cada persona adicional se agrega a la reserva como un "servicio" más, así
// el total, la duración en la agenda, la caja y las comisiones salen solos.
// El número va en el id para que dos personas del mismo tipo no se pisen.
export const itemPersonaAdicional = (opcion, numero) => ({
  id: `domAdic_${opcion.id}_${numero}`,
  name: `Persona adicional · ${opcion.nombre}`,
  price: Number(opcion.precio) || 0,
  duration: Number(opcion.duracion) || 0,
  category: 'A domicilio',
  adicionalDomicilioId: opcion.id,
});

// ¿El servicio es de la categoría a domicilio? Esos solo se ofrecen en "A domicilio"
export const esServicioDomicilio = (servicio, config) =>
  !!config?.categoria && (servicio?.category?.trim() || 'General') === config.categoria.trim();

// Rectángulo que cubre Gran Asunción con un margen (de Limpio a Ñemby,
// del río hasta Capiatá/Areguá). Solo se usa para rechazar un GPS que
// claramente cae en otro lado — la ciudad elegida es lo que manda.
const LIMITES_GRAN_ASUNCION = { latMin: -25.52, latMax: -25.10, lngMin: -57.72, lngMax: -57.33 };

export const dentroDeGranAsuncion = (lat, lng) => {
  const { latMin, latMax, lngMin, lngMax } = LIMITES_GRAN_ASUNCION;
  return lat >= latMin && lat <= latMax && lng >= lngMin && lng <= lngMax;
};

// 🔗 Link de ubicación pegado por el cliente (WhatsApp, Google Maps, Apple
// Maps o coordenadas sueltas) → { lat, lng }, o null si no trae el punto.
// Los links cortos (maps.app.goo.gl) primero pasan por la función
// resolverLinkUbicacion, que devuelve el link largo.
const PATRONES_COORDENADAS = [
  /!3d(-?\d{1,2}\.\d+)!4d(-?\d{1,3}\.\d+)/,                                        // punto exacto del lugar
  /[?&](?:q|query|ll|sll|center|daddr|destination)=(?:loc:)?(-?\d{1,2}\.\d+)\s*,\s*\+?(-?\d{1,3}\.\d+)/i,
  /@(-?\d{1,2}\.\d+),(-?\d{1,3}\.\d+)/,                                             // centro del mapa
  /(-?\d{1,2}\.\d{3,})\s*,\s*\+?\s*(-?\d{1,3}\.\d{3,})/,                            // coordenadas sueltas
];

export const coordenadasDeLink = (texto) => {
  if (!texto) return null;
  let t = String(texto).trim();
  try { t = decodeURIComponent(t); } catch (e) { /* link con % sueltos: se usa tal cual */ }
  for (const patron of PATRONES_COORDENADAS) {
    const m = t.match(patron);
    if (!m) continue;
    const lat = Number(m[1]);
    const lng = Number(m[2]);
    if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) };
  }
  return null;
};

// El link pegado viene dentro de un texto ("Ubicación: https://...") — se saca solo la URL
export const extraerUrl = (texto) => (String(texto || '').match(/https?:\/\/\S+/i) || [''])[0];

export const esLinkCorto = (url) => /^https?:\/\/(maps\.app\.goo\.gl|goo\.gl\/maps)\//i.test(url || '');

// Link de "Cómo llegar": con GPS va al punto exacto; si no, busca la dirección escrita
export const linkMapaDomicilio = (dom) => {
  if (!dom) return '';
  if (typeof dom.lat === 'number' && typeof dom.lng === 'number') {
    return `https://www.google.com/maps/search/?api=1&query=${dom.lat},${dom.lng}`;
  }
  // Link pegado del que no se pudo sacar el punto: se le abre tal cual al profesional
  if (dom.linkUbicacion) return dom.linkUbicacion;
  const texto = [dom.direccion, dom.ciudad, 'Paraguay'].filter(Boolean).join(', ');
  return texto ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(texto)}` : '';
};

export const textoDireccionDomicilio = (dom) =>
  [dom?.direccion, dom?.ciudad].filter(Boolean).join(', ');
